import debounce from "debounce";
import React, { useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { createFastContext } from "../../../../../contexts/createFastContext";
import { MAP_ELEMENT } from "../../../../../contexts/WorldMapContext/constants";
import { CapabilityService } from "../../../../../util/services/capability";
import { IGetTanks, OpService } from "../../../../../util/services/operation";
import { PoiService } from "../../../../../util/services/poi";
import { VesselService } from "../../../../../util/services/vessel";
import { getStore } from "../../../../../util/store/store";
import { ACTION_TYPE, OPERATION_TYPE, TASK_TYPE, taskTypes, TREATMENT_TYPE, treatmentType, VALIDATION_STATUS } from "../../../../../util/varibles/constants";
import {
    IActivityLog,
    ICombineTreatment,
    IFactory,
    IIdName,
    ILevelByVessel,
    IOperation,
    IOperations,
    IPois,
    IServiceTask,
    ISite,
    IStorage,
    ISupportOp,
    ITaskService,
    IUnit,
    IVessel,
    TOperation,
    TTaskTreatment
} from "../../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../../util/varibles/message";
import { IUpdateOp, selectPlan } from "../../../constants";
import { planOpActions } from "../../../reducer";
import { IGetActivityLog } from "../../../saga";

export const SPLIT_KEY = '/'

export interface IUnitWhenEdit extends IUnit {
    isSorting?: boolean
    isLock?: boolean
    index?: number
    tasks?: TTaskTreatment[] | ITaskService[]
    total_weight_text?: string
}

export interface IVesselOfMain {
    id: string
    name: string
    checked: boolean
    ops?: ({ is_delete?: boolean } & IOperation)[]
}

const MethodOfGetLocations: { [id: string]: (op: IOperation) => ISupportOp['locations'] } = {
    Subs: (op) => {
        const { sub_operations } = op.operation;
        const group = sub_operations.reduce((rs: { [id: string]: { name: string, subs: IIdName[] } }, item) => {
            const { site_id = "", site_name = '', id, unit_id } = item;
            if (!rs[site_id])
                rs = { ...rs, [site_id]: { name: site_name, subs: [] } }

            rs[site_id].subs.push({ id, name: unit_id });
            return rs;
        }, {})
        return Object.keys(group).map(key => ({
            id: key,
            name: group[key].name,
            type: MAP_ELEMENT.SITE,
            sub_locations: group[key].subs
        }))
    }
}

const getLocationsOfMainOp = (op: IOperation) => {
    const { operation_type } = op.operation;
    const types: { [id: string]: (op: IOperation) => ISupportOp['locations'] } = {
        [OPERATION_TYPE.HARVEST]: MethodOfGetLocations.Subs,
        [OPERATION_TYPE.TREATMENT]: MethodOfGetLocations.Subs
    }

    const get = types[operation_type];
    return get?.(op) || [];
}

interface IState {
    startTime: number
    opType?: OPERATION_TYPE
    editMode?: ACTION_TYPE
    supportMode: boolean
    isCalculate?: boolean
    loadingSubmit?: boolean

    dragData?: {
        id: string
    }

    count: number
    files: any[]

    bodyGetRoundMap?: IGetTanks
    loadingRoundMap?: boolean

    site?: ISite
    units: { [id: string]: IUnitWhenEdit }

    harvests: ({ isCall?: boolean } & IOperation)[]
    operation?: TOperation
    ops: TOperation[]
    vessel?: IVessel
    activity_log: IActivityLog[]
    operations: IOperations
    level: ILevelByVessel

    pois: {
        loading: boolean
        data: IPois[]
    }

    // Tanks of vessel, Harvest
    vesselTanks: IStorage[]

    // Support Operations
    supportOperations: {
        // Main Op
        [id: string]: {
            // Vessel
            [id: string]: IVesselOfMain
        }
    }

    // Tasks of vessel, Combine Treatment
    vesselTasks: {
        loading: boolean
        data: ICombineTreatment[]
    }
    serviceTasks: {
        loading: boolean
        data: IServiceTask[]
    }

    factories: IFactory[],

    onSave(params: IUpdateOp): void

    onClose(): void
}

const {
    Provider,
    useStore: useEditOp,
    setStore: setEditOp,
    getStore: getEditOp,
    callEvent: callEventEditOp
} = createFastContext<IState, {
    getLocations(): void
    getServiceTasks(vessel: { id: string, capabilities: IVessel['capabilities'] }): void
    getVesselTasks(vessel: { id: string, operation_sub_types: TREATMENT_TYPE[] }): void
    getRoundMap(params?: Partial<IGetTanks>): void
    turnOnCalculate(): void
    getActivityLog(props: IGetActivityLog): void
    handeUpdateOp(value: Partial<TOperation>): void
    handleSave(params: IUpdateOp): void
}>({
    startTime: 0,
    supportMode: false,

    count: 0,
    files: [],

    units: {},

    harvests: [],
    ops: [],
    activity_log: [],
    operations: {},
    level: {},

    pois: {
        loading: false,
        data: [],
    },
    vesselTasks: {
        loading: false,
        data: [],
    },
    serviceTasks: {
        loading: false,
        data: []
    },
    vesselTanks: [],

    supportOperations: {},

    factories: [],

    onSave: () => null,
    onClose: () => null
});


const EditOperationProvider = React.memo(({ children, opType, operation, vessel, activity_log, onClose, ...args }: {
    children: React.ReactNode
} & Partial<IState>) => {
    const dispatch = useDispatch();
    const roundMapControl = useRef<AbortController | null>(null);
    const callRoundMap = useRef(debounce((body: IGetTanks, set: ReturnType<typeof setEditOp>) => set(() => {
        if (roundMapControl.current)
            roundMapControl.current.abort();
        roundMapControl.current = new AbortController();

        OpService.tanks(body, roundMapControl.current)
            .then(({ round_map, duration }) => {
                set(({ operation }) => ({
                    operation: operation
                        ? { ...operation, round_map, duration }
                        : operation,
                    loadingRoundMap: false
                }))
            })
            .catch(err => {
                console.log(err)
                set({ loadingRoundMap: false })
            })
        return { loadingRoundMap: true }
    }), 500));

    const newState: Partial<IState> = useMemo(() => {
        const { related_id = "", checklist_ids = [] } = operation || {};

        if (opType === OPERATION_TYPE.SUPPORT) {
            if (related_id.length > 0) {
                const rootOps = selectPlan.ops(getStore());
                const mainOp = rootOps[related_id]
                if (mainOp)
                    return {
                        operation: mainOp.operation,
                        vessel: mainOp.vessel,
                        activity_log: mainOp.activity_log,
                        supportMode: true,
                        opType: mainOp.operation.operation_type,
                        onClose
                    }
            } else {
                onClose?.();
                notify.error(NotifyCode.E3)();
            }
        }
        return { operation, vessel, activity_log, opType, init: checklist_ids.length === 0, onClose }
    }, [operation, opType, vessel, activity_log, onClose])

    return <Provider
        props={{ ...args, ...newState }}
        event={{
            getLocations: ({ set }) => {
                set(({ pois }) => {
                    if (pois.loading || pois.data.length > 0)
                        return { pois }
                    PoiService.gets()
                        .then(rs => set({ pois: { loading: false, data: rs } }))
                        .catch(error => {
                            console.log('Get pois failed', error);
                            set({ pois: { loading: false, data: [] } })
                        })
                    return { pois: { ...pois, loading: true } }
                });
            },
            getRoundMap: (params, { get, set }) => {
                const { startTime, bodyGetRoundMap = {}, vessel, isCalculate } = get();
                const body = {
                    ...bodyGetRoundMap,
                    ...params,
                    vessel_id: vessel?.id,
                    start_time: startTime,
                } as IGetTanks;
                if (JSON.stringify(body) !== JSON.stringify(bodyGetRoundMap)) {
                    set({ bodyGetRoundMap: body });
                    if (!isCalculate)
                        return;

                    callRoundMap.current(body, set);
                }
            },
            getVesselTasks: ({ id, operation_sub_types }, { set }) => {
                const sub_types = Array.from(operation_sub_types).sort((a: any, b: any) => a - b);
                const staticTasks = [
                    ...sub_types.map(item => ({
                        ...treatmentType[item],
                        id: [TASK_TYPE.TREATMENT, item].join(SPLIT_KEY),
                    })),
                    taskTypes[TASK_TYPE.MOVE],
                ] as ICombineTreatment[]

                set(({ vesselTasks }) => {
                    if (vesselTasks.loading || vesselTasks.data.length > 0)
                        return { vesselTasks }

                    VesselService.getTreatmentTypes(id)
                        .then(rs => {
                            const { groups, elements } = rs;
                            const treatmentOfTenant = new Set([...elements.map(item => [TASK_TYPE.TREATMENT, item].join(SPLIT_KEY)), TASK_TYPE.MOVE]);
                            const listOfCheck = new Set(staticTasks.map(item => item.id));
                            const validCombineTask = groups.reduce((list: ICombineTreatment[], item) => {
                                const { sub_types } = item;
                                const isInvalid = sub_types.some(sub => !listOfCheck.has([sub.type, sub.treatment_type].join('/')));
                                if (!isInvalid) {
                                    list.push({ ...item, id: [TASK_TYPE.COMBINE, item.id].join('/') });
                                }
                                return list;
                            }, []);
                            set({
                                vesselTasks: {
                                    loading: false,
                                    data: [...validCombineTask, ...staticTasks.filter(item => treatmentOfTenant.has(item.id))]
                                }
                            })
                        })
                        .catch(error => {
                            console.log('Get combine treatment failed', error);
                            set({
                                vesselTasks: {
                                    loading: false,
                                    data: staticTasks
                                }
                            })
                        })
                    return { vesselTasks: { ...vesselTasks, loading: true } }
                })
            },
            getServiceTasks: ({ capabilities }, { set }) => {
                const ids = new Set(capabilities.map(item => item.id));
                set({ serviceTasks: { loading: true, data: [] } });
                CapabilityService.serviceTasks()
                    .then(rs => {
                        const result = rs.filter(item => {
                            const { capabilities = [] } = item;
                            if (capabilities.length === 0)
                                return true
                            else
                                return capabilities.every(sub => ids.has(sub.id))
                        })
                        set({ serviceTasks: { loading: false, data: result } });
                    })
                    .catch(error => {
                        console.log('Get service tasks failed', error);
                        set({ serviceTasks: { loading: false, data: [] } })
                    })
            },
            turnOnCalculate: ({ set }) => set({ isCalculate: true }),
            getActivityLog: ({ source, properties }, { get, set }) => {
                set({ loadingSubmit: true })
                const { editMode, onSave } = get();
                dispatch(planOpActions.getActivityLog({
                    source,
                    properties,
                    skipDurationEst: editMode === ACTION_TYPE.CREATE,
                    success: (ops) => {
                        set({ loadingSubmit: false })
                        onSave(editMode === ACTION_TYPE.CREATE ? { add: ops } : { update: ops });
                    },
                    failure: () => {
                        set({ loadingSubmit: false })
                    }
                }))
            },
            handeUpdateOp: (value: Partial<TOperation>, { set }) => {
                set(({ operation }) => {
                    if (operation)
                        return { operation: { ...operation || {}, ...value } }
                    return { operation }
                })
            },
            handleSave: (params, { get }) => {
                const { supportOperations, editMode, onSave } = get();
                const { add = {}, update = {}, ...subParams } = params;
                let addSp = {}, updateSp = {};

                const action: { [id: string]: (op: IOperation, args: Partial<ISupportOp>) => void } = {
                    [ACTION_TYPE.CREATE]: (op, args) => {
                        addSp = { ...addSp, [op.operation.id]: { ...op, operation: { ...op.operation, ...args } } }
                    },
                    [ACTION_TYPE.EDIT]: (op, args) => {
                        updateSp = { ...updateSp, [op.operation.id]: { ...op, operation: { ...op.operation, ...args } } }
                    }
                }
                let deleteIds: { [id: string]: string[] } = {};
                const checkDelete = (mainId: string, op: IOperation) => {
                    deleteIds = { ...deleteIds, [mainId]: [...deleteIds[mainId] || [], op.operation.id] }
                }
                let errorOps: string[] = [];
                Object.keys(supportOperations).forEach(key => {
                    const spVessels = supportOperations[key];
                    const mainOp = { ...add, ...update }[key];
                    const locations = getLocationsOfMainOp(mainOp);
                    const sub_ids: string[] = [];
                    Object.keys(spVessels).forEach(subKey => {
                        const vessel = spVessels[subKey]
                        const { checked, ops = [] } = vessel;
                        if (checked) {
                            ops.forEach(op => {
                                const { action_type, is_delete, status } = op;
                                if (status === VALIDATION_STATUS.ERROR) {
                                    errorOps.push(`#${op.operation.operation_code}`);
                                }
                                if (is_delete) {
                                    checkDelete(key, op)
                                } else {
                                    const { [action_type || ""]: handle } = action;
                                    if (handle)
                                        handle(op, { locations, related_id: key });
                                    sub_ids.push(op.operation.id);
                                }
                            })
                        } else {
                            ops.forEach(op => checkDelete(key, op))
                        }
                    })
                    if (sub_ids.length > 0) {
                        const { [editMode || ""]: handle } = action;
                        handle(mainOp, { related_id: key });
                    } else if ((mainOp.operation.related_id || "").length > 0) {
                        delete (mainOp.operation as any).related_id;
                    }
                })
                if (errorOps.length > 0) {
                    notify.error(NotifyCode.E48)([errorOps.join(', ')])
                    return;
                }
                onSave({ ...subParams, add: { ...add, ...addSp }, update: { ...update, ...updateSp }, deleteIds })
            }
        }}
    >
        {children}
    </Provider>
}, (prev, next) => prev.operation === next.operation
    && prev.vessel === next.vessel
    && prev.activity_log === next.activity_log
    && prev.operations === next.operations
    && prev.level === next.level);

export { callEventEditOp, EditOperationProvider, getEditOp, setEditOp, useEditOp };
